import React from 'react';

// Section 4 — Research & Dispatches: asymmetric mosaic (featured keynote + 2x2 tiles).
export const ResearchDispatchesSection = ({ onOpenDispatch }) => (
  <section className="py-24 sm:py-32 px-6 sm:px-10 lg:px-16 border-b border-stone-200/80 bg-[#FFFFFF]">
    <div id="systems" className="scroll-mt-20 w-full max-w-[1600px] mx-auto">

      <div className="max-w-3xl mb-12 sm:mb-16">
        <span className="mono text-[10px] uppercase tracking-widest text-[#F58220] font-semibold block mb-2.5">NEWS &middot; PROOF OF WORK</span>
        <h2 className="text-3xl sm:text-5xl font-medium tracking-tight text-stone-950 mb-3.5">What we&#8217;re building, debating, and proving right.</h2>
        <p className="text-base sm:text-lg text-stone-700 leading-relaxed font-sans max-w-2xl">
          Deep-tech engineering, live fund deployments, and real-world AI adoption across the deal lifecycle.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">

        {/* Featured anchor: Korea Defense Forum keynote */}
        <div className="lg:col-span-6 flex flex-col">
          <a href="https://www.linkedin.com/feed/update/urn:li:activity:7474688189882986496/" target="_blank" rel="noopener noreferrer" className="mosaic-tile group rounded-3xl p-8 sm:p-10 flex flex-col justify-between h-full min-h-[600px] sm:min-h-[720px] lg:min-h-full border border-black/10 shadow-sm cursor-pointer relative overflow-hidden bg-[#090E1A] transition-all duration-300 hover:border-white/20">
            <img
              src="https://media.licdn.com/dms/image/v2/D4E22AQEwNSkz8QMB9w/feedshare-shrink_1280/B4EZ7ttSa4HkAM-/0/1782104532947?e=1794441600&v=beta&t=EkQLodOAs3zJK-_RcVXZLT3Se4QLX4Rfz9KVvA9dKw0"
              alt="Balbinder Singh Bhatia Keynote Address at Korea Defense & Security Forum"
              className="absolute inset-0 w-full h-full object-cover object-center opacity-90 group-hover:opacity-100 group-hover:scale-[1.02] filter contrast-[105%] transition-all duration-700 pointer-events-none"
              onError={(e) => { e.currentTarget.src = 'https://placehold.co/1200x1400/090E1A/FFFFFF?text=Korea+Keynote'; }}
            />
            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#090E1A] via-[#090E1A]/85 to-transparent pointer-events-none" />
            <div className="flex-1 min-h-[260px] pointer-events-none" />
            <div className="relative z-10 pt-6">
              <div className="text-xs mono text-stone-300 mb-2 font-medium">Jun 2026 · Seoul</div>
              <h3 className="text-2xl sm:text-3xl font-bold text-white tracking-tight leading-snug group-hover:text-stone-200 transition-colors drop-shadow-md">
                Sovereign Intelligence in Mission-Critical Strategy: Keynote to the Korea Defense & Security Forum
              </h3>
            </div>
          </a>
        </div>

        {/* 2x2 grid */}
        <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-6">

          {/* OmniContext paper (external) */}
          <a
            href="https://www.linkedin.com/pulse/omnicontext-hybrid-context-retrieval-engine-turns-almost-zuhaib-ahmed-bhxnc/"
            target="_blank" rel="noopener noreferrer"
            className="mosaic-tile bg-[#090E1A] group rounded-3xl p-7 sm:p-8 flex flex-col justify-between min-h-[350px] sm:min-h-[370px] border border-black/10 shadow-sm cursor-pointer relative overflow-hidden hover:border-white/20 transition-all duration-300"
          >
            <img
              src="https://images.unsplash.com/photo-1506146332389-18140dc7b2fb?q=80&w=1000&auto=format&fit=crop"
              alt="Deep-Tech Engineering Geometry"
              className="absolute inset-0 w-full h-full object-cover object-center opacity-35 group-hover:opacity-45 group-hover:scale-[1.03] filter contrast-125 brightness-75 transition-all duration-700 pointer-events-none"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#090E1A] via-[#090E1A]/80 to-transparent pointer-events-none" />
            <div className="my-auto py-8 flex flex-col items-center justify-center relative z-10 select-none text-center">
              <div className="mono text-2xl sm:text-3xl text-white font-extrabold tracking-tight">
                Omni<span className="text-stone-400">Context</span>™
              </div>
              <span className="mono text-[9px] text-stone-400 mt-1 uppercase tracking-widest">Hybrid Retrieval Engine</span>
            </div>
            <div className="relative z-10 pt-4">
              <div className="text-[11px] mono text-stone-400 mb-1.5 font-medium">Aug 2026 · Zuhaib Ahmed</div>
              <h4 className="text-base font-semibold text-white tracking-tight leading-snug group-hover:text-stone-200 transition-colors">
                The Hybrid Retrieval Engine That Turns "Almost Right" Into "Always Right"
              </h4>
            </div>
          </a>

          {/* IdeaSpring Capital case brief */}
          <div
            onClick={() => onOpenDispatch('IdeaSpring Capital Case Brief')}
            className="mosaic-tile bg-[#090E1A] group rounded-3xl p-7 sm:p-8 flex flex-col justify-between min-h-[350px] sm:min-h-[370px] border border-black/10 shadow-sm cursor-pointer relative overflow-hidden hover:border-white/20 transition-all duration-300"
          >
            <img
              src="https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=1000&auto=format&fit=crop"
              alt="Venture Capital Deep-Tech Lab"
              className="absolute inset-0 w-full h-full object-cover object-center opacity-40 group-hover:opacity-50 group-hover:scale-[1.03] filter contrast-125 brightness-75 transition-all duration-700 pointer-events-none"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#090E1A] via-[#090E1A]/80 to-transparent pointer-events-none" />
            <div className="my-auto py-8 flex flex-col items-center justify-center relative z-10 select-none text-center">
              <div className="flex items-center gap-1.5 opacity-90 group-hover:opacity-100 transition-opacity">
                <span className="font-extrabold text-2xl sm:text-3xl tracking-tight text-white">IdeaSpring</span>
                <span className="font-light tracking-widest text-2xl sm:text-3xl text-stone-300">CAPITAL</span>
              </div>
              <span className="mono text-[9px] text-stone-400 mt-1 uppercase tracking-widest">Deep-Tech Venture Capital</span>
            </div>
            <div className="relative z-10 pt-4">
              <div className="text-[11px] mono text-stone-400 mb-1.5 font-medium">Oct 2026 · Bengaluru</div>
              <h4 className="text-base font-semibold text-white tracking-tight leading-snug group-hover:text-stone-200 transition-colors">
                Automating Deep-Tech PortCo Monitoring & Strategic Exit Positioning
              </h4>
            </div>
          </div>

          {/* Executive AI Masterclass */}
          <div
            onClick={() => onOpenDispatch('Executive AI Masterclass Brief')}
            className="mosaic-tile bg-[#090E1A] group rounded-3xl p-7 sm:p-8 flex flex-col justify-between min-h-[350px] sm:min-h-[370px] border border-black/10 shadow-sm cursor-pointer relative overflow-hidden hover:border-white/20 transition-all duration-300"
          >
            <img
              src="https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?q=80&w=1000&auto=format&fit=crop"
              alt="Executive Strategy Masterclass Chamber"
              className="absolute inset-0 w-full h-full object-cover object-center opacity-35 group-hover:opacity-45 group-hover:scale-[1.03] filter contrast-125 brightness-75 transition-all duration-700 pointer-events-none"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#090E1A] via-[#090E1A]/80 to-transparent pointer-events-none" />
            <div className="my-auto py-8 flex flex-col items-center justify-center relative z-10 select-none text-center">
              <div className="font-extrabold text-2xl sm:text-3xl tracking-[0.2em] text-white uppercase font-sans">EXECUTIVE LAB</div>
              <span className="mono text-[9px] text-stone-400 mt-1 uppercase tracking-widest">Strategy & M&A Masterclass</span>
            </div>
            <div className="relative z-10 pt-4">
              <div className="text-[11px] mono text-stone-400 mb-1.5 font-medium">Sep 2026 · Live Workflow Demo</div>
              <h4 className="text-base font-semibold text-white tracking-tight leading-snug group-hover:text-stone-200 transition-colors">
                AI for Strategy & M&A Executives: Orchestrating Autonomous Workflows from Screening to Exit
              </h4>
            </div>
          </div>

          {/* European cross-border M&A monograph */}
          <div
            onClick={() => onOpenDispatch('European Strategy Monograph')}
            className="mosaic-tile bg-[#090E1A] group rounded-3xl p-7 sm:p-8 flex flex-col justify-between min-h-[350px] sm:min-h-[370px] border border-black/10 shadow-sm cursor-pointer relative overflow-hidden hover:border-white/20 transition-all duration-300"
          >
            <img
              src="https://images.unsplash.com/photo-1512453979798-5ea266f8880c?q=80&w=1000&auto=format&fit=crop"
              alt="European Private Capital Financial Plaza"
              className="absolute inset-0 w-full h-full object-cover object-center opacity-40 group-hover:opacity-50 group-hover:scale-[1.03] filter contrast-125 brightness-75 transition-all duration-700 pointer-events-none"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#090E1A] via-[#090E1A]/80 to-transparent pointer-events-none" />
            <div className="my-auto py-8 flex items-center justify-center relative z-10 select-none text-center">
              <div className="font-extrabold text-2xl sm:text-3xl tracking-[0.22em] text-white/40 uppercase font-sans group-hover:text-white/60 transition-colors">LUXEMBOURG</div>
            </div>
            <div className="relative z-10 pt-4">
              <div className="text-[11px] mono text-stone-400 mb-1.5 font-medium">May 2026 · Continental Expansion</div>
              <h4 className="text-base font-semibold text-white tracking-tight leading-snug group-hover:text-stone-200 transition-colors">
                Dr. Bharat Madan Joins Frontier as President to Lead European Expansion from Luxembourg
              </h4>
            </div>
          </div>

        </div>
      </div>
    </div>
  </section>
);
